import React, { useState, useEffect } from "react";
import StatusPill from "../components/common/StatusPill";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ConsultationDetailsDialog from "../components/consultations/ConsultationDetailsDialog";
import { motion } from "framer-motion";
import {
  MessageSquare,
  Clock,
  User,
  Calendar,
  Video,
  Phone,
  AlertCircle,
  CheckCircle,
  XCircle,
  Play,
  Pause,
  MoreVertical,
  Search,
  Filter,
  RefreshCw,
  Copy,
  CalendarDays,
  Loader2,
  Eye,
} from "lucide-react";
import LoadingSpinner from "../components/common/LoadingSpinner";
import consultationService from "../services/consultationService";

// shadcn components
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";

import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";

const ConsultationsShadcn = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { consultationId: routeConsultationId } = useParams();
  const [detailId, setDetailId] = useState(routeConsultationId || null);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;
  const [lastDoc, setLastDoc] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [period, setPeriod] = useState("week"); // day | week | month | all

  // Set initial filter based on route
  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/consultations/upcoming")) {
      setStatusFilter("upcoming");
    } else if (path.includes("/consultations/active")) {
      setStatusFilter("active");
    } else if (path.includes("/consultations/completed")) {
      setStatusFilter("completed");
    } else if (path.includes("/consultations/past")) {
      setStatusFilter("past");
    } else if (path.includes("/consultations/abandoned")) {
      setStatusFilter("abandoned");
    } else if (path.includes("/consultations/cancelled")) {
      setStatusFilter("cancelled");
    } else {
      setStatusFilter("all");
    }
  }, [location.pathname]);

  const statusOptions = [
    { value: "all", label: "All Consultations", count: 0 },
    { value: "upcoming", label: "Upcoming", count: 0 },
    { value: "active", label: "Active", count: 0 },
    { value: "completed", label: "Completed", count: 0 },
    { value: "past", label: "Past", count: 0 },
    { value: "abandoned", label: "Abandoned", count: 0 },
    { value: "cancelled", label: "Cancelled", count: 0 },
  ];

  const sourceOptions = [
    { value: "all", label: "All Sources" },
    { value: "app", label: "App" },
    { value: "web", label: "Web" },
  ];

  useEffect(() => {
    fetchConsultations();
  }, [statusFilter, sourceFilter, selectedDate, period]);

  // week = last 7 days, month = last 30 days, both ending today
  const periodRange = () => {
    if (period === "all") return { start: 0, end: 8640000000000000 };
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - (period === "week" ? 6 : 29));
    return { start: start.getTime(), end: end.getTime() };
  };

  const periodLabel = () =>
    period === "day" ? formatDate(selectedDate) : period === "week" ? "the last 7 days" : period === "month" ? "the last 30 days" : "all time";

  const fetchConsultations = async (loadMore = false) => {
    try {
      setLoading(true);
      setError(null);

      // Always fetch consultations by date, then filter client-side
      // Pull every page for the period; the table paginates client-side (12 per page).
      const raw = [];
      let cursor = null;
      for (let i = 0; i < 100; i++) {
        let result;
        if (period === "day") {
          result = await consultationService.getConsultationsByDate(selectedDate, 200, cursor);
        } else {
          const { start, end } = periodRange();
          result = await consultationService.getConsultationsInRange(start, end, 200, cursor);
        }
        raw.push(...result.consultations);
        cursor = result.lastVisible;
        if (!result.hasMore) break;
      }

      const formattedConsultations = raw.map((consultation) =>
        consultationService.formatConsultationData(consultation)
      );

      // Filter consultations based on status
      let filteredConsultations = formattedConsultations;
      if (statusFilter !== "all") {
        filteredConsultations = formattedConsultations.filter(
          (consultation) => consultation.category === statusFilter
        );
      }

      if (sourceFilter !== "all") {
        filteredConsultations = filteredConsultations.filter(
          (consultation) => {
            const currentSource = (consultation.meetLink ? "web" : consultation.booking_type) === "web" ? "web" : "app";
            return currentSource === sourceFilter;
          }
        );
      }

      setConsultations(filteredConsultations);
      setCurrentPage(1);
      setLastDoc(null);
      setHasMore(false);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching consultations:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setLastDoc(null);
    setCurrentPage(1);
    setSelectedDate(new Date()); // Reset to today's date
    setPeriod("week");
    fetchConsultations();
  };

  const handleViewDetails = (consultation) => {
    setDetailId(consultation.id);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleCopyConsultationId = async (consultationId) => {
    try {
      await navigator.clipboard.writeText(consultationId);
      setCopiedId(consultationId);
      setTimeout(() => {
        setCopiedId(null);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy consultation ID:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = consultationId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedId(consultationId);
      setTimeout(() => {
        setCopiedId(null);
      }, 2000);
    }
  };

  const filteredConsultations = consultations.filter((consultation) => {
    const matchesSearch =
      consultation.patientName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      consultation.doctorName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      consultation.consultationId
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const totalPages = Math.max(1, Math.ceil(filteredConsultations.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedConsultations = filteredConsultations.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, sourceFilter, period, selectedDate]);


  const getStatusIcon = (status) => {
    switch (status) {
      case "Completed":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "In Progress":
        return <Play className="h-4 w-4 text-blue-600" />;
      case "Started":
        return <Pause className="h-4 w-4 text-yellow-600" />;
      case "Active":
        return <Play className="h-4 w-4 text-purple-600" />;
      case "Upcoming":
        return <Clock className="h-4 w-4 text-cyan-600" />;
      case "Past":
        return <Clock className="h-4 w-4 text-gray-600" />;
      case "Abandoned":
        return <AlertCircle className="h-4 w-4 text-orange-600" />;
      case "Scheduled":
        return <Clock className="h-4 w-4 text-gray-600" />;
      case "Cancelled":
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const statusTone = (status) =>
    ({ Completed: "green", "In Progress": "blue", Active: "violet", Started: "amber", Upcoming: "blue", Abandoned: "amber", Cancelled: "red" }[status] || "neutral");
  const statusIcon = (status) =>
    ({ Completed: CheckCircle, "In Progress": Play, Active: Play, Started: Pause, Upcoming: Clock, Past: Clock, Scheduled: Clock, Abandoned: AlertCircle, Cancelled: XCircle }[status] || AlertCircle);

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "Completed":
        return "default";
      case "In Progress":
      case "Active":
        return "secondary";
      case "Upcoming":
        return "outline";
      case "Past":
        return "secondary";
      case "Abandoned":
        return "destructive";
      case "Cancelled":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getConsultationTypeIcon = (consultation) => {
    if (consultation.videoConsultDone) {
      return <Video className="h-4 w-4" />;
    }
    return <Phone className="h-4 w-4" />;
  };

  if (loading && consultations.length === 0) {
    return <LoadingSpinner fullHeight message="Fetching consultation data from Soocher..." />;
  }

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 6 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "hsl(var(--primary))", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <MessageSquare size={14} color="white" />
            </div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, color: "hsl(var(--foreground))", letterSpacing: "-0.4px" }}>Consultations</h1>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: "hsl(var(--muted-foreground))" }}>
            for {periodLabel()} • {consultations.length} total
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "inline-flex", padding: 3, borderRadius: 10, background: "hsl(var(--muted))", border: "1px solid hsl(var(--border))" }}>
            {[["day", "Day"], ["week", "Week"], ["month", "Month"], ["all", "All"]].map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => { setLastDoc(null); setPeriod(v); }}
                style={{ height: 28, padding: "0 14px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 600, fontFamily: "'IBM Plex Sans',sans-serif", background: period === v ? "hsl(var(--card))" : "transparent", color: period === v ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))", boxShadow: period === v ? "0 1px 2px rgba(0,0,0,.08)" : "none" }}
              >{l}</button>
            ))}
          </div>
          {period === "day" && <Popover>
            <PopoverTrigger asChild>
              <button style={{ height: 36, padding: "0 14px", borderRadius: 9, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--card))", fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <CalendarDays size={14} color="#3b82f6" />
                {formatDate(selectedDate)}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <CalendarComponent
                mode="single"
                selected={selectedDate}
                onSelect={(date) => { if (date) setSelectedDate(date); }}
                initialFocus
                className="rounded-md border"
              />
            </PopoverContent>
          </Popover>}
          <button
            onClick={handleRefresh}
            disabled={loading}
            style={{ height: 36, padding: "0 14px", borderRadius: 9, border: "1.5px solid hsl(var(--tint-blue-border))", background: "hsl(var(--muted) / 0.6)", fontSize: 13, fontWeight: 600, color: "hsl(var(--muted-foreground))", display: "flex", alignItems: "center", gap: 6, cursor: loading ? "not-allowed" : "pointer" }}
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-card" style={{ padding: "14px 18px", marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: 1, minWidth: 220, position: "relative" }}>
          <Search size={13} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "hsl(var(--muted-foreground))" }} />
          <input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search consultations..."
            style={{ width: "100%", paddingLeft: 30, paddingRight: 12, height: 36, borderRadius: 9, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--muted))", fontSize: 12.5, color: "hsl(var(--foreground))", outline: "none", fontFamily: "'IBM Plex Sans',sans-serif", boxSizing: "border-box" }}
            onFocus={e => { e.currentTarget.style.borderColor = "#3b82f6"; e.currentTarget.style.background = "hsl(var(--card))"; }}
            onBlur={e => { e.currentTarget.style.borderColor = "hsl(var(--input))"; e.currentTarget.style.background = "hsl(var(--muted))"; }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Filter size={14} color="hsl(var(--muted-foreground))" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger style={{ width: 168, height: 36, borderRadius: 9, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--card))", fontSize: 12.5, fontFamily: "'IBM Plex Sans',sans-serif" }}>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger style={{ width: 120, height: 36, borderRadius: 9, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--card))", fontSize: 12.5, fontFamily: "'IBM Plex Sans',sans-serif" }}>
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              {sourceOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div style={{ padding: "12px 16px", borderRadius: 10, background: "hsl(var(--tint-red))", border: "1px solid hsl(var(--tint-red-border))", color: "#ef4444", fontSize: 13, display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <AlertCircle size={16} />
          <span style={{ flex: 1 }}>{error}</span>
          <button onClick={handleRefresh} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid hsl(var(--tint-red-border))", background: "hsl(var(--card))", color: "#ef4444", fontSize: 12, cursor: "pointer" }}>Retry</button>
        </div>
      )}

      {/* Consultations Table */}
      <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
        {filteredConsultations.length === 0 ? (
          <div style={{ padding: "60px 24px", textAlign: "center" }}>
            <MessageSquare size={40} style={{ color: "hsl(var(--input))", margin: "0 auto 12px" }} />
            <p style={{ fontSize: 15, fontWeight: 600, color: "hsl(var(--foreground))" }}>No consultations found</p>
            <p style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>
              {searchTerm ? "No consultations match your search criteria" : "No consultations available at the moment"}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead>
                <tr style={{ background: "hsl(var(--muted) / 0.5)", borderBottom: "1.5px solid hsl(var(--border))" }}>
                  {["Consultation ID", "Patient", "Doctor", "Scheduled Time", "Amount paid", "Status", "Meet", "Actions"].map(h => (
                    <th key={h} style={{ padding: "11px 14px", fontSize: 10.5, fontWeight: 700, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagedConsultations.map((consultation, idx) => (
                  <tr
                    key={consultation.id}
                    style={{ borderBottom: "1px solid hsl(var(--muted))", background: idx % 2 === 0 ? "hsl(var(--card))" : "hsl(var(--muted) / 0.4)", transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "hsl(var(--tint-violet))"}
                    onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "hsl(var(--card))" : "hsl(var(--muted) / 0.4)"}
                  >
                    <td style={{ padding: "12px 14px", fontFamily: "monospace", fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>
                      #{consultation.consultationId?.slice(-8) || "N/A"}
                    </td>
                    <td style={{ padding: "12px 14px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Avatar className="h-7 w-7 ring-1 ring-blue-100">
                          <AvatarFallback className="bg-blue-50 text-blue-700 text-xs font-semibold">
                            {consultation.patientName?.charAt(0) || "P"}
                          </AvatarFallback>
                        </Avatar>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))" }}>{consultation.patientName || "N/A"}</span>
                      </div>
                    </td>
                    <td style={{ padding: "12px 14px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Avatar className="h-7 w-7 ring-1 ring-green-100">
                          <AvatarFallback className="bg-green-50 text-green-700 text-xs font-semibold">
                            {consultation.doctorName?.charAt(0) || "D"}
                          </AvatarFallback>
                        </Avatar>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))" }}>{consultation.doctorName || "N/A"}</span>
                      </div>
                    </td>
                    <td style={{ padding: "12px 14px", fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Calendar size={12} color="hsl(var(--muted-foreground))" />
                        {consultation.formattedConsultationTime}
                      </div>
                    </td>
                    <td style={{ padding: "12px 14px" }}>
                      {(() => {
                        const paid = Number(consultation.payment?.amountPaise || 0) / 100;
                        const free = consultation.payment?.status === "free" || paid === 0;
                        return (
                          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            <span className="num" style={{ fontSize: 12.5, fontWeight: 600, color: free ? "hsl(var(--muted-foreground))" : "hsl(var(--foreground))" }}>
                              {free ? "Free" : `₹${paid.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
                            </span>
                            {consultation.appliedCoupon && (
                              <StatusPill tone="violet">{consultation.appliedCoupon}{Number(consultation.couponDiscount) > 0 ? ` −₹${Number(consultation.couponDiscount)}` : ""}</StatusPill>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ padding: "12px 14px" }}>
                      <StatusPill tone={statusTone(consultation.status)} icon={statusIcon(consultation.status)}>{consultation.status}</StatusPill>
                    </td>
                    <td style={{ padding: "12px 14px" }}>
                      {consultation.meetLink ? (
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(consultation.meetLink);
                            setCopiedId(`meet-${consultation.id}`);
                            setTimeout(() => setCopiedId(null), 2000);
                          }}
                          style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid hsl(var(--tint-green-border))", background: "hsl(var(--tint-green))", color: "#059669", fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", textDecoration: "none" }}
                          onMouseEnter={e => { e.currentTarget.style.background = "hsl(var(--tint-green))"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "hsl(var(--tint-green))"; }}
                        >
                          {copiedId === `meet-${consultation.id}` ? <CheckCircle size={12} /> : <Copy size={12} />} {copiedId === `meet-${consultation.id}` ? "Copied" : <><Video size={12} /> Copy</>}
                        </button>
                      ) : (
                        <span style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: "12px 14px" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          onClick={() => handleViewDetails(consultation)}
                          style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid hsl(var(--input))", background: "hsl(var(--tint-blue))", color: "#2563eb", fontSize: 11, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", transition: "all 0.15s" }}
                          onMouseEnter={e => { e.currentTarget.style.background = "hsl(var(--tint-blue))"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "hsl(var(--tint-blue))"; }}
                        >
                          <Eye size={12} /> Details
                        </button>
                        <button
                          onClick={() => handleCopyConsultationId(consultation.consultationId)}
                          style={{ width: 24, height: 24, borderRadius: 6, border: "1px solid hsl(var(--border))", background: copiedId === consultation.consultationId ? "#ecfdf5" : "transparent", color: copiedId === consultation.consultationId ? "#10b981" : "hsl(var(--muted-foreground))", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.15s" }}
                          onMouseEnter={e => { if (copiedId !== consultation.consultationId) { e.currentTarget.style.background = "hsl(var(--muted))"; e.currentTarget.style.color = "hsl(var(--foreground))"; } }}
                          onMouseLeave={e => { if (copiedId !== consultation.consultationId) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "hsl(var(--muted-foreground))"; } }}
                          title="Copy ID"
                        >
                          {copiedId === consultation.consultationId ? <CheckCircle size={12} /> : <Copy size={12} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderTop: "1px solid hsl(var(--muted))" }}>
            <span style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>
              Showing <span className="num">{(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filteredConsultations.length)}</span> of <span className="num">{filteredConsultations.length}</span>
            </span>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <button
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={safePage === 1}
                style={{ padding: "5px 12px", borderRadius: 8, border: "1.5px solid hsl(var(--input))", background: safePage === 1 ? "hsl(var(--muted) / 0.5)" : "hsl(var(--card))", fontSize: 12, color: safePage === 1 ? "hsl(var(--muted-foreground))" : "hsl(var(--primary))", cursor: safePage === 1 ? "not-allowed" : "pointer", fontFamily: "'IBM Plex Sans',sans-serif" }}
              >← Prev</button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const n = totalPages <= 5 ? i + 1 : Math.min(Math.max(1, safePage - 2), totalPages - 4) + i;
                return (
                  <button key={n} onClick={() => setPage(n)} className="num"
                    style={{ width: 30, height: 30, borderRadius: 8, border: `1.5px solid ${safePage === n ? "hsl(var(--primary))" : "hsl(var(--input))"}`, background: safePage === n ? "hsl(var(--primary))" : "hsl(var(--card))", color: safePage === n ? "hsl(var(--primary-foreground))" : "hsl(var(--foreground))", fontSize: 12, fontWeight: safePage === n ? 700 : 400, cursor: "pointer" }}>
                    {n}
                  </button>
                );
              })}
              <button
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                disabled={safePage === totalPages}
                style={{ padding: "5px 12px", borderRadius: 8, border: "1.5px solid hsl(var(--input))", background: safePage === totalPages ? "hsl(var(--muted) / 0.5)" : "hsl(var(--card))", fontSize: 12, color: safePage === totalPages ? "hsl(var(--muted-foreground))" : "hsl(var(--primary))", cursor: safePage === totalPages ? "not-allowed" : "pointer", fontFamily: "'IBM Plex Sans',sans-serif" }}
              >Next →</button>
            </div>
          </div>
        )}
      </div>

      <ConsultationDetailsDialog
        consultationId={detailId}
        onClose={() => {
          setDetailId(null);
          if (routeConsultationId) navigate("/consultations");
        }}
      />
    </div>
  );
};

export default ConsultationsShadcn;
